// Slim the `messages` array on outbound WS bodies before sending it as LLM
// context.
//
// The backend orchestrator stamps the raw hotel/flight API response into the
// prompt history as `{ role: "assistant", content: "<JSON-stringified response>" }`
// (see `LLM-function/services/cloudService.js` push points). On every
// subsequent turn the frontend relays that full prompt history back over the
// WS, so the same 30+ KB API blob gets shipped again and again. A lot of that
// data is irrelevant to ANY user query — image URLs the LLM can't view,
// encrypted booking blobs, supplier-side monitoring metrics, internal scoring
// engine outputs. Dropping those is safe regardless of what the user asks.
//
// Approach is deliberately SUBTRACTIVE. We don't pre-shape the output for any
// specific user query (like "show me cheaper" or "any direct flights"). The
// user might ask about anything — fare basis, equipment type, terminal,
// layover times, amenities, hotel description, distance, refund policy — so
// we keep every field except those that are universally useless. The
// trade-off vs an additive whitelist is smaller % reduction, but zero risk of
// the LLM saying "I don't have that information" on a query we didn't predict.
//
// What we drop and why:
//
//   1. Image URLs (`airlineImage`, `hotelImages`, `hotelMainImage`,
//      `marketingAirlineImage`, `operatingAirlineImage`) — the LLM cannot
//      view images. URLs add ~80-100 B each and arrays of 20-40 per hotel
//      account for most of the hotel-response bulk.
//
//   2. Encrypted/internal IDs (`fareId`, `uniqueId`, `unicaId`,
//      `transactionId`, `encryptedKey`, `sessionId`, `sessionExpiresAt`,
//      `propertyTypeId`, `hotelChainId`, `hotelId`) — base64 blobs up to 280
//      chars. The LLM can't reason about opaque identifiers. The backend
//      still has them in its own state machine.
//
//   3. Scoring engine internals (`score`, `rank`, `violations`,
//      `scoreBreakdown`, `scoringV1`, `weightage`, `totalWeightage`, `boost`,
//      `preferred`, `filteredOut`, `hardFiltered`, `configSource`) — pure
//      backend ranking infrastructure. The ORDER of the array already
//      reflects the scoring; the per-result score metadata is redundant.
//
//   4. Supplier monitoring (`monitoring_details`,
//      `supplierExecutionTimes`, `supplier`, `supplier_name`,
//      `supplier_type`) — aggregator-side observability. The user shouldn't
//      know whether their booking went through MONDEE vs NUITEE.
//
//   5. Internal pricing breakdown (`supplierFare`, `exchangeRate`,
//      `supplierCurrency`, `gdsIncentive`, `paxPrice`, `psup_fare`,
//      `otCharges`, `auto_discount`) — the user-facing `totalFare` /
//      `pricePerRoom.price` / `currency` are kept. The supplier-cost
//      breakdown is internal.
//
//   6. Hotel `hotelDetails` wrapper — almost every field inside is
//      duplicated against its sibling at the result level (name, address,
//      stars, rating, etc.). We LIFT the two genuinely unique fields
//      (`aboutProperty`, `importantInformation`) out before dropping.
//
//   7. `facets.amenities` and `facets.suppliers` — huge counts dicts that
//      sum frequencies across the result set. Useful only for filter UIs,
//      not for the LLM to reason about a specific hotel.
//
// What we KEEP (deliberately, including things that look like fluff):
//   - All amenities per hotel (full list, even COVID-era operational items —
//     the LLM might be asked about anti-virus measures)
//   - `aboutProperty` HTML (full marketing description — the LLM may need it
//     to describe a hotel)
//   - Per-segment flight detail: equipment type, terminal, segment-level
//     airport names, layover dates/times, cabin type
//   - `farebasisCode`, `fareFamilyCode`, `rbd`, `cabinType` — fare class
//     metadata; the LLM may explain "this is a basic-economy fare"
//   - All pricing-display fields (totalPrice, pricePerRoom, currency, tax)
//   - All schedule fields (UTC + local + timezone)
//   - Facets that are useful summary stats (starRatings, guestRatingBuckets,
//     propertyTypes, cancellationTypes, mealPlans)

import { encode as toonEncode } from "@toon-format/toon";

const FIELDS_TO_DROP: ReadonlySet<string> = new Set([
  // Scoring engine internals — the array order already encodes the ranking
  "score",
  "rank",
  "violations",
  "scoreBreakdown",
  "scoringV1",
  "weightage",
  "totalWeightage",
  "boost",
  "preferred",
  "filteredOut",
  "hardFiltered",
  "configSource",

  // Supplier monitoring / aggregator metadata
  "monitoring_details",
  "supplierExecutionTimes",
  "supplier",
  "supplier_name",
  "supplier_type",

  // Image URLs (LLM cannot view images)
  "airlineImage",
  "marketingAirlineImage",
  "operatingAirlineImage",
  "hotelImages",
  "hotelMainImage",

  // Encrypted / opaque internal IDs
  "transactionId",
  "fareId",
  "uniqueId",
  "unicaId",
  "encryptedKey",
  "sessionId",
  "sessionExpiresAt",
  "propertyTypeId",
  "hotelChainId",
  "hotelId",

  // Internal pricing breakdown — user-facing price/totalFare/currency are kept
  "supplierFare",
  "exchangeRate",
  "supplierCurrency",
  "gdsIncentive",
  "paxPrice",
  "psup_fare",
  "otCharges",
  "auto_discount",
]);

interface ChatMessage {
  role: string;
  content: string;
  [k: string]: any;
}

export interface SanitizeOptions {
  /**
   * When true, hotel/flight API blobs in assistant `content` strings are
   * re-encoded as TOON (Token-Oriented Object Notation) instead of being
   * re-stringified as JSON. TOON's tabular form ([N]{keys}:rows) is ~40-45%
   * more token-efficient than JSON for arrays of homogeneous objects (which
   * sectors[], hotels[], segments[], stops[] all are). The OUTER message
   * envelope stays JSON — only the inner `content` representation changes.
   *
   * Backend requires no change: `content` is an opaque string fed straight
   * into the LLM prompt as the prior assistant turn. The LLM parses TOON
   * natively (the format is self-describing via the `[N]{keys}:` headers).
   */
  useToon?: boolean;
}

// Top-level entry: returns a shallow copy of `body` with `body.messages`
// rewritten to slim form. If `body.messages` is absent or not an array, the
// original body is returned as-is.
export function sanitizeBodyForLLM(
  body: any,
  options: SanitizeOptions = {},
): any {
  if (!body || typeof body !== "object") return body;
  const messages = (body as any).messages;
  if (!Array.isArray(messages)) return body;
  return {
    ...body,
    messages: messages.map((m) => sanitizeMessage(m, options)),
  };
}

function sanitizeMessage(
  msg: ChatMessage,
  options: SanitizeOptions,
): ChatMessage {
  if (!msg || typeof msg !== "object") return msg;
  if (msg.role !== "assistant") return msg;
  if (typeof msg.content !== "string") return msg;

  const trimmed = msg.content.trim();
  // Skip anything under 500 bytes — it's not a fat API blob, just chat text.
  if (trimmed.length < 500) return msg;
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return msg;

  let parsed: any;
  try {
    parsed = JSON.parse(msg.content);
  } catch {
    return msg; // not JSON, leave it alone
  }

  if (!looksLikeFatApiResponse(parsed)) return msg;

  // Deep blacklist-drop the universal noise.
  const stripped = deepStripInternalFields(parsed);
  // Hotel-specific tidy: lift two unique fields out of `hotelDetails`, then
  // drop the wrapper (the rest of its keys duplicate the sibling fields).
  liftHotelDescription(stripped);
  // Hotel-specific tidy: facets has two enormous sub-dicts that aren't
  // useful for per-result reasoning.
  slimFacets(stripped);

  return { ...msg, content: serializeContent(stripped, options) };
}

// Re-encode the sanitised structure for transport. TOON when enabled (token
// win at the LLM), JSON otherwise. TOON failures fall back to JSON so a
// malformed structure never breaks the send path — the worst case is missing
// out on the token savings for that one message.
//
// TOON encoder tuning for LLM input:
//   - `indent: 0` removes leading whitespace per nesting level (~25% byte
//     reduction over default `indent: 2`). The LLM doesn't need visual
//     indentation — TOON's tabular `[N]{keys}:` headers and `- ` list markers
//     already carry the structure.
//   - `keyFolding: "safe"` collapses single-key wrapper chains into dotted
//     paths (`data.metadata.items` instead of three nested levels). Lossless
//     and only fires when valid identifiers, so it never produces ambiguous
//     output.
const TOON_ENCODE_OPTIONS = { indent: 0, keyFolding: "safe" } as const;

function serializeContent(value: any, options: SanitizeOptions): string {
  if (!options.useToon) return JSON.stringify(value);
  try {
    return toonEncode(value, TOON_ENCODE_OPTIONS);
  } catch {
    return JSON.stringify(value);
  }
}

// Only kick in for the shapes we know carry the heavy blobs. Other JSON-ish
// assistant content (e.g. small structured tool replies) passes through.
function looksLikeFatApiResponse(obj: any): boolean {
  if (!obj || typeof obj !== "object") return false;
  // Hotel search response: top-level `data` array with city objects holding
  // a `hotels` array.
  if (
    Array.isArray(obj.data) &&
    obj.data.length > 0 &&
    Array.isArray(obj.data[0]?.hotels)
  ) {
    return true;
  }
  // Flight search response: top-level `sectors` array, each with outbound/inbound.
  if (
    Array.isArray(obj.sectors) &&
    obj.sectors.length > 0 &&
    (Array.isArray(obj.sectors[0]?.outbound) ||
      Array.isArray(obj.sectors[0]?.inbound))
  ) {
    return true;
  }
  return false;
}

// Walk the object recursively, returning a new object with blacklisted keys
// removed at every depth. Preserves arrays, primitives, and field order.
function deepStripInternalFields(obj: any): any {
  if (Array.isArray(obj)) return obj.map(deepStripInternalFields);
  if (obj === null || typeof obj !== "object") return obj;
  const out: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (FIELDS_TO_DROP.has(key)) continue;
    out[key] = deepStripInternalFields(value);
  }
  return out;
}

// Mutating tidy on the already-stripped hotel response. Cheaper than a
// second deep walk. The hotel result's `hotelDetails` field is a near-total
// duplicate of its sibling fields — same name, address, stars, rating,
// reviewCount, etc. The only fields that exist ONLY inside hotelDetails are
// `aboutProperty` (HTML marketing description) and `importantInformation`
// (often empty, but the API populates it for some properties). We lift
// those out, then drop the wrapper.
function liftHotelDescription(obj: any): void {
  if (!Array.isArray(obj?.data)) return;
  for (const city of obj.data) {
    if (!Array.isArray(city?.hotels)) continue;
    for (const h of city.hotels) {
      if (!h || typeof h !== "object" || !h.hotelDetails) continue;
      const hd = h.hotelDetails;
      if (hd.aboutProperty != null && h.aboutProperty == null) {
        h.aboutProperty = hd.aboutProperty;
      }
      if (hd.importantInformation != null && h.importantInformation == null) {
        h.importantInformation = hd.importantInformation;
      }
      delete h.hotelDetails;
    }
  }
}

// `facets.amenities` is a huge { amenity-name: count } dict (~30 entries,
// ~1 KB) — useful for filter UI sliders, not for per-result LLM reasoning.
// `facets.suppliers` is internal-only. Drop those two, keep the rest of
// facets (starRatings, guestRatingBuckets, propertyTypes, mealPlans,
// cancellationTypes, priceRange) which give the LLM a feel for the overall
// search result distribution.
function slimFacets(obj: any): void {
  if (!obj?.facets || typeof obj.facets !== "object") return;
  delete obj.facets.amenities;
  delete obj.facets.suppliers;
}
